import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { EntityType, Entity, AttributeDataType, QueryFilter } from '../types';
import { loadEntityAttributes, loadFkAttributeGroups, discoverJsonKeysFromEntities } from '../utils/discoverAttributes';
import { limsClient } from '../api/limsClient';
import './Modal.css';
import './EntityFilters.css';

interface EntityFiltersProps {
  entityType: EntityType;
  filters?: QueryFilter[];
  onFiltersChange: (filters: QueryFilter[]) => void;
  /** Entities for JSON key auto-discovery. */
  entities?: Entity[];
}

interface FilterableAttribute {
  name: string;
  attribute_name: string;
  data_type: AttributeDataType;
  groupId: string;
  groupLabel: string;
  /** For cross-entity attrs: the entity type to fetch suggestions from. */
  refEntityType?: EntityType;
  json_path?: string;
}

interface AttrGroup {
  id: string;
  label: string;
  items: FilterableAttribute[];
}

type OperatorOption = QueryFilter['operator'] | 'BETWEEN';

const OPERATORS: Record<AttributeDataType, OperatorOption[]> = {
  text: ['=', '!=', 'LIKE', 'ILIKE', 'IN', 'IS NULL', 'IS NOT NULL'],
  number: ['=', '!=', '>', '>=', '<', '<=', 'IN', 'IS NULL', 'IS NOT NULL'],
  boolean: ['=', '!='],
  date: ['=', '>', '>=', '<', '<=', 'BETWEEN', 'IS NULL', 'IS NOT NULL'],
  json: ['=', '!=', 'IS NULL', 'IS NOT NULL'],
};

const formatFilterDisplay = (f: QueryFilter): string => {
  const name = f.json_path ? `${f.attribute_name}.${f.json_path}` : f.attribute_name;
  if (Array.isArray(f.or) && f.or.length > 0) {
    const clauses = f.or.map(c => {
      if (c.operator === 'IS NULL' || c.operator === 'IS NOT NULL') return c.operator;
      const v = Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '');
      return `${c.operator} ${v}`;
    });
    return `${name} ${clauses.join(' OR ')}`;
  }
  if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') return `${name} ${f.operator}`;
  const val = Array.isArray(f.value) ? f.value.join(', ') : String(f.value ?? '');
  return `${name} ${f.operator} ${val}`;
};


export const EntityFilters: React.FC<EntityFiltersProps> = ({
  entityType,
  filters: externalFilters,
  onFiltersChange,
  entities: sampleEntities = [],
}) => {
  const [appliedFilters, setAppliedFilters] = useState<QueryFilter[]>(externalFilters || []);
  const [open, setOpen] = useState(false);
  const [allAttrs, setAllAttrs] = useState<FilterableAttribute[]>([]);
  const [attrSearch, setAttrSearch] = useState('');
  const [selectedAttr, setSelectedAttr] = useState<FilterableAttribute | null>(null);
  const [operator, setOperator] = useState<OperatorOption>('=');
  const [value, setValue] = useState<string>('');
  const [betweenFrom, setBetweenFrom] = useState('');
  const [betweenTo, setBetweenTo] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const pendingEditRef = useRef<QueryFilter | null>(null);

  useEffect(() => {
    const incoming = externalFilters || [];
    if (JSON.stringify(incoming) !== JSON.stringify(appliedFilters)) {
      setAppliedFilters(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFilters]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { directAttrs, fkAttrs, jsonAttrNames } = await loadEntityAttributes(entityType);
        if (cancelled) return;

        const result: FilterableAttribute[] = [];
        result.push({
          name: 'id', attribute_name: 'id', data_type: 'number',
          groupId: 'direct', groupLabel: 'Direct attributes',
        });
        for (const a of directAttrs) {
          result.push({
            name: a.name, attribute_name: a.name, data_type: a.data_type,
            groupId: 'direct', groupLabel: 'Direct attributes',
          });
        }

        const fkGroups = await loadFkAttributeGroups(fkAttrs, () => cancelled);
        if (cancelled) return;
        for (const g of fkGroups) {
          for (const ra of g.attrs) {
            result.push({
              name: ra.name,
              attribute_name: `${g.fkAttrName}.${ra.name}`,
              data_type: ra.data_type,
              groupId: g.id,
              groupLabel: g.label,
              refEntityType: g.refType,
            });
          }
        }

        if (jsonAttrNames.length > 0) {
          const discovered = discoverJsonKeysFromEntities(sampleEntities, jsonAttrNames, entityType);
          for (const { attrName, keys } of discovered) {
            for (const k of keys) {
              result.push({
                name: k.key,
                attribute_name: attrName,
                data_type: 'text',
                groupId: `json_${attrName}`,
                groupLabel: `${attrName} (via ${entityType})`,
                json_path: k.key,
              });
            }
          }
        }

        if (!cancelled) setAllAttrs(result);
      } catch (err) {
        console.warn('Failed to load attributes for filter modal:', err);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, entityType, sampleEntities]);

  useEffect(() => {
    if (allAttrs.length > 0 && pendingEditRef.current) {
      const f = pendingEditRef.current;
      pendingEditRef.current = null;
      prepopulateFromFilter(f, allAttrs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAttrs]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setEditingIndex(null); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const hasSearch = attrSearch.trim().length > 0;

  const groups = useMemo<AttrGroup[]>(() => {
    const map = new Map<string, AttrGroup>();
    const search = attrSearch.toLowerCase();
    for (const attr of allAttrs) {
      if (search && !attr.name.toLowerCase().includes(search)) continue;
      if (!map.has(attr.groupId)) {
        map.set(attr.groupId, { id: attr.groupId, label: attr.groupLabel, items: [] });
      }
      map.get(attr.groupId)!.items.push(attr);
    }
    return Array.from(map.values());
  }, [allAttrs, attrSearch]);

  const selectAttribute = (attr: FilterableAttribute) => {
    setSelectedAttr(attr);
    const ops = OPERATORS[attr.data_type] || ['='];
    setOperator(ops[0]);
    setValue('');
    setBetweenFrom('');
    setBetweenTo('');
    setEditingIndex(null);
  };

  const findAttrMatch = (f: QueryFilter, attrs: FilterableAttribute[]): FilterableAttribute | undefined => {
    return attrs.find(a =>
      a.attribute_name === f.attribute_name &&
      (a.json_path ?? null) === (f.json_path ?? null)
    );
  };

  const prepopulateFromFilter = (f: QueryFilter, attrs: FilterableAttribute[]) => {
    const attr = findAttrMatch(f, attrs);
    if (!attr) return;
    setSelectedAttr(attr);
    setOperator(f.operator as OperatorOption);
    if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') {
      setValue('');
    } else if (Array.isArray(f.value)) {
      setValue(f.value.join(', '));
    } else {
      setValue(f.value != null ? String(f.value) : '');
    }
    setBetweenFrom('');
    setBetweenTo('');
  };

  const handleEditPill = (index: number) => {
    const f = appliedFilters[index];
    if (!f) return;
    setEditingIndex(index);
    setAttrSearch('');
    setExpanded({});

    if (allAttrs.length > 0) {
      prepopulateFromFilter(f, allAttrs);
      setOpen(true);
    } else {
      pendingEditRef.current = f;
      setSelectedAttr(null);
      setValue('');
      setBetweenFrom('');
      setBetweenTo('');
      setOpen(true);
    }
  };

  const handleAddFilter = () => {
    if (!selectedAttr) return;

    const base: Partial<QueryFilter> = {
      attribute_name: selectedAttr.attribute_name,
    };
    if (selectedAttr.json_path) base.json_path = selectedAttr.json_path;

    let newFilters: QueryFilter[];

    if (operator === 'BETWEEN') {
      newFilters = [];
      if (betweenFrom) {
        newFilters.push({ ...base, operator: '>=', value: betweenFrom } as QueryFilter);
      }
      if (betweenTo) {
        newFilters.push({ ...base, operator: '<=', value: betweenTo } as QueryFilter);
      }
      if (newFilters.length === 0) return;
    } else if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
      newFilters = [{ ...base, operator } as QueryFilter];
    } else if (operator === 'IN') {
      const parts = value.split(',').map(v => v.trim()).filter(Boolean);
      if (parts.length === 0) return;
      const parsed = selectedAttr.data_type === 'number'
        ? parts.map(v => parseFloat(v)).filter(v => !isNaN(v))
        : parts;
      if (parsed.length === 0) return;
      newFilters = [{ ...base, operator, value: parsed } as QueryFilter];
    } else {
      if (!value && value !== '0') return;
      let finalValue: QueryFilter['value'] = value;
      if (selectedAttr.data_type === 'number') {
        const num = parseFloat(value);
        if (isNaN(num)) return;
        finalValue = num;
      } else if (selectedAttr.data_type === 'boolean') {
        finalValue = value === 'true';
      }
      newFilters = [{ ...base, operator: operator as QueryFilter['operator'], value: finalValue } as QueryFilter];
    }

    let updated: QueryFilter[];
    if (editingIndex !== null) {
      updated = [...appliedFilters];
      updated.splice(editingIndex, 1, ...newFilters);
      setEditingIndex(null);
    } else {
      updated = [...appliedFilters, ...newFilters];
    }
    setAppliedFilters(updated);
    onFiltersChange(updated);

    setValue('');
    setBetweenFrom('');
    setBetweenTo('');
  };

  const removeAppliedPill = (index: number) => {
    if (editingIndex !== null) {
      if (index === editingIndex) {
        setEditingIndex(null);
      } else if (index < editingIndex) {
        setEditingIndex(editingIndex - 1);
      }
    }
    const updated = appliedFilters.filter((_, i) => i !== index);
    setAppliedFilters(updated);
    onFiltersChange(updated);
  };

  const clearFilters = () => {
    setAppliedFilters([]);
    onFiltersChange([]);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditingIndex(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleCloseModal();
  };

  const handleOpenModal = () => {
    setAttrSearch('');
    setSelectedAttr(null);
    setValue('');
    setBetweenFrom('');
    setBetweenTo('');
    setExpanded({});
    setEditingIndex(null);
    pendingEditRef.current = null;
    setOpen(true);
  };

  const toggleGroup = (groupId: string) => {
    setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const operatorOptions = selectedAttr ? (OPERATORS[selectedAttr.data_type] || ['=']) : [];
  const needsValue = operator !== 'IS NULL' && operator !== 'IS NOT NULL';
  const isBetween = operator === 'BETWEEN';

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<(string | number)[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = useCallback((attr: FilterableAttribute, search?: string) => {
    if (attr.json_path) {
      setSuggestions([]);
      return;
    }
    const targetEntityType = attr.refEntityType ?? entityType;
    const attrName = attr.attribute_name.includes('.') ? attr.attribute_name.split('.', 2)[1] : attr.attribute_name;
    setSuggestionsLoading(true);
    limsClient.getAttributeValues(targetEntityType, attrName, search || undefined, 50)
      .then(result => {
        setSuggestions(result.values);
        setSuggestionsLoading(false);
      })
      .catch(() => {
        setSuggestions([]);
        setSuggestionsLoading(false);
      });
  }, [entityType]);

  const handleValueChangeWithSuggestions = useCallback((newValue: string) => {
    setValue(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (selectedAttr && !selectedAttr.json_path) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(selectedAttr, newValue);
      }, 300);
    }
  }, [selectedAttr, fetchSuggestions]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, [selectedAttr]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const renderAutocompleteInput = (placeholder: string) => {
    if (!selectedAttr) return null;
    return (
      <div className="fm-autocomplete-wrapper" ref={suggestionsRef}>
        <input
          type="text"
          className="fm-value-input"
          value={value}
          onChange={e => handleValueChangeWithSuggestions(e.target.value)}
          onFocus={() => {
            setShowSuggestions(true);
            if (suggestions.length === 0) fetchSuggestions(selectedAttr, value || undefined);
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {showSuggestions && (suggestions.length > 0 || suggestionsLoading) && (
          <div className="fm-suggestions-dropdown">
            {suggestionsLoading && suggestions.length === 0 && (
              <div className="fm-suggestion-loading">Loading...</div>
            )}
            {suggestions.map((s, i) => (
              <div
                key={i}
                className={`fm-suggestion-item${String(s) === value ? ' active' : ''}`}
                onMouseDown={e => {
                  e.preventDefault();
                  setValue(String(s));
                  setShowSuggestions(false);
                }}
              >
                {String(s)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderValueInput = () => {
    if (!selectedAttr || !needsValue) return null;

    if (isBetween) {
      return (
        <div className="fm-between-row">
          <input
            type="datetime-local"
            className="fm-value-input fm-date-input"
            value={betweenFrom}
            onChange={e => setBetweenFrom(e.target.value)}
          />
          <span className="fm-between-label">to</span>
          <input
            type="datetime-local"
            className="fm-value-input fm-date-input"
            value={betweenTo}
            onChange={e => setBetweenTo(e.target.value)}
          />
        </div>
      );
    }

    if (selectedAttr.data_type === 'boolean') {
      return (
        <select
          className="fm-value-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        >
          <option value="">Select...</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (selectedAttr.data_type === 'date') {
      return (
        <input
          type="datetime-local"
          className="fm-value-input fm-date-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        />
      );
    }

    if ((selectedAttr.data_type === 'text' || selectedAttr.data_type === 'number') && !selectedAttr.json_path) {
      const placeholder = operator === 'IN'
        ? (selectedAttr.data_type === 'number' ? 'e.g. 1, 2, 3' : 'e.g. a, b, c')
        : (selectedAttr.data_type === 'number' ? 'Enter number...' : 'Enter value...');
      return renderAutocompleteInput(placeholder);
    }

    const placeholder = operator === 'IN'
      ? (selectedAttr.data_type === 'number' ? 'e.g. 1, 2, 3' : 'e.g. a, b, c')
      : 'Enter value...';

    return (
      <input
        type="text"
        className="fm-value-input"
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={placeholder}
      />
    );
  };

  return (
    <div className="entity-filters">
      {appliedFilters.length > 0 && (
        <div className="applied-pills">
          {appliedFilters.map((f, i) => (
            <span key={i} className="applied-pill" onClick={() => handleEditPill(i)} title="Click to edit">
              <span className="applied-pill-text">{formatFilterDisplay(f)}</span>
              <button className="applied-pill-remove" onClick={e => { e.stopPropagation(); removeAppliedPill(i); }} title="Remove filter">&times;</button>
            </span>
          ))}
        </div>
      )}

      <div className="filters-toolbar">
        <button onClick={handleOpenModal} className="add-filter-btn">+ Filter</button>
        {appliedFilters.length > 0 && (
          <button onClick={clearFilters} className="clear-filters-btn">Clear all</button>
        )}
      </div>

      {open && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-container fm-modal" role="dialog" aria-modal="true" aria-label={editingIndex !== null ? 'Edit filter' : 'Add filter'}>
            <div className="modal-header">
              <h3>{editingIndex !== null ? 'Edit Filter' : 'Add Filter'}</h3>
              <button className="modal-close" onClick={handleCloseModal} aria-label="Close">&times;</button>
            </div>

            {appliedFilters.length > 0 && (
              <div className="fm-applied">
                {appliedFilters.map((f, i) => (
                  <span
                    key={i}
                    className={`applied-pill${editingIndex === i ? ' editing' : ''}`}
                    onClick={() => { setEditingIndex(i); prepopulateFromFilter(f, allAttrs); }}
                    title="Click to edit"
                  >
                    <span className="applied-pill-text">{formatFilterDisplay(f)}</span>
                    <button className="applied-pill-remove" onClick={e => { e.stopPropagation(); removeAppliedPill(i); }} title="Remove filter">&times;</button>
                  </span>
                ))}
              </div>
            )}

            <div className="fm-body">
              <div className="fm-left">
                <div className="fm-search-wrapper">
                  <svg className="fm-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="6.5" cy="6.5" r="5" />
                    <line x1="10" y1="10" x2="15" y2="15" />
                  </svg>
                  <input
                    type="text"
                    className="fm-search-input"
                    placeholder="Search attributes..."
                    value={attrSearch}
                    onChange={e => setAttrSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="fm-attr-list">
                  {groups.length === 0 && (
                    <div className="fm-empty">No matching attributes</div>
                  )}
                  {groups.map(group => (
                    <div key={group.id} className="modal-group">
                      <button className="modal-group-header" onClick={() => toggleGroup(group.id)}>
                        <span className={`modal-group-arrow ${(hasSearch || expanded[group.id]) ? 'expanded' : ''}`}>&#9656;</span>
                        <span className="modal-group-label">{group.label}</span>
                        <span className="modal-group-count">{group.items.length}</span>
                      </button>
                      {(hasSearch || expanded[group.id]) && (
                        <div className="fm-group-items">
                          {group.items.map(attr => {
                            const key = `${attr.groupId}_${attr.attribute_name}_${attr.json_path || ''}`;
                            const isActive = selectedAttr && key === `${selectedAttr.groupId}_${selectedAttr.attribute_name}_${selectedAttr.json_path || ''}`;
                            return (
                              <div
                                key={key}
                                className={`fm-attr-item ${isActive ? 'active' : ''}`}
                                onClick={() => selectAttribute(attr)}
                              >
                                <span className="fm-attr-name">{attr.name}</span>
                                <span className="data-type-badge" data-type={attr.data_type}>{attr.data_type}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="fm-right">
                {!selectedAttr ? (
                  <div className="fm-placeholder">Select an attribute from the left to build a filter</div>
                ) : (
                  <div className="fm-form">
                    <div className="fm-form-attr">
                      <span className="fm-form-attr-name">{selectedAttr.name}</span>
                      {selectedAttr.refEntityType && <span className="fm-form-attr-via">via {selectedAttr.refEntityType}</span>}
                      {selectedAttr.json_path && <span className="fm-form-attr-via">in {selectedAttr.attribute_name}</span>}
                      <span className="data-type-badge" data-type={selectedAttr.data_type}>{selectedAttr.data_type}</span>
                    </div>

                    <label className="fm-label">Operator</label>
                    <select
                      className="fm-operator-select"
                      value={operator}
                      onChange={e => {
                        setOperator(e.target.value as OperatorOption);
                        setValue('');
                        setBetweenFrom('');
                        setBetweenTo('');
                      }}
                    >
                      {operatorOptions.map(op => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>

                    {needsValue && (
                      <>
                        <label className="fm-label">{isBetween ? 'Range' : 'Value'}</label>
                        {renderValueInput()}
                      </>
                    )}

                    <button className="fm-add-btn" onClick={handleAddFilter}>
                      {editingIndex !== null ? 'Update Filter' : 'Add Filter'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="fm-done" onClick={handleCloseModal}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
